import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, FileText, ImageIcon, Link as LinkIcon, Save, Loader2, Upload, Zap, ShieldCheck } from 'lucide-react'
import { mediaService } from '../../services/mediaService'
import { useAuth } from '../../contexts/AuthContext'

interface ProfileMetadata {
    name?: string
    display_name?: string
    about?: string
    picture?: string
    website?: string
    lud16?: string
    nip05?: string
}

interface ProfileEditModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (metadata: ProfileMetadata) => Promise<void>
    initialData: ProfileMetadata | null
}

export function ProfileEditModal({ isOpen, onClose, onSave, initialData }: ProfileEditModalProps) {
    const [formData, setFormData] = useState<ProfileMetadata>(initialData || {})
    const [isSaving, setIsSaving] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { sk, loginMethod } = useAuth()

    // Sync formData when initialData changes or modal opens
    useEffect(() => {
        if (isOpen && initialData) {
            setFormData(initialData)
        }
    }, [isOpen, initialData])

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !loginMethod) return

        // Reset input
        e.target.value = ''

        // 10MB limit
        if (file.size > 10 * 1024 * 1024) {
            alert('Dosya boyutu çok büyük (Maksimum 10MB).')
            return
        }

        setIsUploading(true)
        try {
            const url = await mediaService.uploadFile(file, loginMethod, sk || undefined)
            setFormData(prev => ({ ...prev, picture: url }))
        } catch (error: any) {
            console.error('Image upload failed:', error)
            alert(error.message || 'Resim yüklenemedi. Lütfen tekrar deneyin.')
        } finally {
            setIsUploading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSaving(true)
        try {
            await onSave(formData)
            onClose()
        } catch (error) {
            console.error('Failed to save profile:', error)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] cursor-pointer"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
                        className="fixed top-1/2 left-1/2 w-full max-w-lg bg-white dark:bg-surface-900 rounded-[2rem] shadow-2xl z-[110] overflow-hidden border border-surface-100 dark:border-surface-800"
                    >
                        {/* Header */}
                        <div className="bg-mavi-600 p-6 flex items-center justify-between text-white relative overflow-hidden">
                            <div className="relative z-10">
                                <h2 className="font-poppins font-bold text-xl">Profili Düzenle</h2>
                                <p className="text-[10px] text-white/70 font-poppins uppercase tracking-widest mt-1">Kimliğini güncelle</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="relative z-20 p-2 hover:bg-white/10 rounded-xl transition-all"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
                            {/* Name & Display Name */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                        <User className="w-3 h-3" /> Kullanıcı Adı
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-poppins"
                                        placeholder="kullanici_adi"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                        <User className="w-3 h-3" /> Görünen İsim
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.display_name || ''}
                                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                                        className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-poppins"
                                        placeholder="Ad Soyad"
                                    />
                                </div>
                            </div>

                            {/* Picture URL & Upload */}
                            <div className="space-y-2">
                                <label className="flex items-center justify-between text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <span className="flex items-center gap-2"><ImageIcon className="w-3 h-3" /> Profil Resmi</span>
                                    {isUploading && <span className="text-mavi-500 flex items-center gap-1 normal-case"><Loader2 className="w-3 h-3 animate-spin" /> Yükleniyor...</span>}
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1 relative">
                                        <input
                                            type="url"
                                            value={formData.picture || ''}
                                            onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                                            className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-mono"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="p-4 bg-mavi-500 hover:bg-mavi-600 text-white rounded-2xl transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                                        title="Resim Yükle"
                                    >
                                        <Upload className="w-5 h-5" />
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            {/* Bio / About */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <FileText className="w-3 h-3" /> Hakkında
                                </label>
                                <textarea
                                    value={formData.about || ''}
                                    onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-merriweather italic h-32 resize-none"
                                    placeholder="Sözlüğe renk katan bir şeyler yaz..."
                                />
                            </div>

                            {/* Website */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <LinkIcon className="w-3 h-3" /> Web Sitesi
                                </label>
                                <input
                                    type="url"
                                    value={formData.website || ''}
                                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-mono"
                                    placeholder="https://yourpage.com"
                                />
                            </div>

                            {/* Lightning Address (lud16) */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <Zap className="w-3 h-3" /> Bitcoin Lightning Address
                                </label>
                                <input
                                    type="text"
                                    value={formData.lud16 || ''}
                                    onChange={(e) => setFormData({ ...formData, lud16: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-mono"
                                    placeholder="Enter your LN address"
                                />
                            </div>

                            {/* NIP-05 Verification */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <ShieldCheck className="w-3 h-3" /> Verified Nostr Address (NIP-05)
                                </label>
                                <input
                                    type="text"
                                    value={formData.nip05 || ''}
                                    onChange={(e) => setFormData({ ...formData, nip05: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-mono"
                                    placeholder="Enter your NIP-05 Verified Nostr ID"
                                />
                            </div>

                            {/* Footer Actions */}
                            <div className="pt-6 flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-4 px-6 rounded-2xl bg-surface-100 dark:bg-surface-800 text-gray-700 dark:text-gray-300 font-poppins font-bold text-sm hover:bg-surface-200 dark:hover:bg-surface-700 transition-all"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="flex-[2] py-4 px-6 rounded-2xl bg-mavi-600 text-white font-poppins font-bold text-sm hover:bg-mavi-700 transition-all shadow-lg shadow-mavi-500/30 flex items-center justify-center gap-2"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Yayınlanıyor...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Değişiklikleri Kaydet
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
